'use client';

import { useAuth } from '@/hooks/useAuth';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useState, useRef, useMemo } from 'react';
import { Loader2, Landmark, Trash2, Handshake, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Image from '@/components/ui/Image';
import { useToast } from '@/hooks/use-toast';
import { createOrganizacionSocialPost, createSocialStory, deleteSocialPost, deleteSocialStory, acceptCollaboration, rejectCollaboration } from '@/lib/actions';
import { Organizacion, SocialPost, SocialStory, JartinetHermandad, Banda } from '@/lib/types';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { MediaUploader } from '@/components/social/MediaUploader';
import { SongPicker } from '@/components/social/SongPicker';
import { MentionTextarea } from '@/components/social/MentionTextarea';
import { useCollection } from '@/firebase/firestore/use-collection';
import { collection, query, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { Link } from 'react-router-dom';

function CreatePostForm({ organizaciones, allHermandades, allBandas, onFormSuccess }: { organizaciones: Organizacion[], allHermandades: JartinetHermandad[], allBandas: Banda[], onFormSuccess: () => void }) {
    const { toast } = useToast();
    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
  
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(event.currentTarget);
        const result = await createOrganizacionSocialPost(null, formData);

        if (result.success) {
          toast({ title: "Éxito", description: result.message });
          formRef.current?.reset();
          onFormSuccess();
        } else {
          toast({ title: "Error", description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

  return (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="organizacionId">Organización</Label>
            <Select name="organizacionId" required>
              <SelectTrigger><SelectValue placeholder="Elige una organización..." /></SelectTrigger>
              <SelectContent>
                {organizaciones.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.nombreCompleto}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Título</Label>
            <Input id="title" name="title" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="content">Contenido</Label>
            <MentionTextarea id="content" name="content" placeholder="Escribe aquí el contenido... usa @ para mencionar" />
          </div>
          <SongPicker />
          <MediaUploader label="Fotos de la publicación" max={10} />
          <div className="space-y-2">
              <Label htmlFor="collaboratorId">Invitar a colaborar (Opcional)</Label>
              <Select name="collaboratorId">
                  <SelectTrigger><SelectValue placeholder="Elige un perfil..." /></SelectTrigger>
                  <SelectContent>
                      <SelectItem value="none">No colaborar</SelectItem>
                      {allHermandades.map(h => (
                          <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                              {h.nombreCompleto}
                            </div>
                          </SelectItem>
                      ))}
                      {allBandas.map(b => (
                          <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                            <div className="flex items-center gap-2">
                               <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                               {b.nombreCompleto}
                            </div>
                          </SelectItem>
                      ))}
                  </SelectContent>
              </Select>
          </div>
           <DialogFooter>
             <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Publicar
             </Button>
          </DialogFooter>
        </form>
  );
}

function CreateStoryForm({ organizaciones, onFormSuccess }: { organizaciones: Organizacion[], onFormSuccess: () => void }) {
    const formRef = useRef<HTMLFormElement>(null);
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
  
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const organizacionId = formData.get('organizacionId') as string;
        if (!organizacionId) {
            toast({ title: "Error", description: "Debes seleccionar una organización.", variant: 'destructive' });
            return;
        }

        setIsSubmitting(true);
        const result = await createSocialStory(organizacionId, 'organizacion', formData);
  
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            formRef.current?.reset();
            onFormSuccess();
        } else {
            toast({ title: "Error", description: result.message || 'Ocurrió un error', variant: 'destructive' });
        }
        setIsSubmitting(false);
    }
  
    return (
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="organizacionId">Organización</Label>
          <Select name="organizacionId" required>
            <SelectTrigger><SelectValue placeholder="Elige una organización..." /></SelectTrigger>
            <SelectContent>
              {organizaciones.map(org => (
                <SelectItem key={org.id} value={org.id}>{org.nombreCompleto}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <MediaUploader single required label="Foto de la historia" />
         <div className="space-y-2">
          <Label htmlFor="text">Texto (Opcional)</Label>
          <RichTextarea id="text" name="text" placeholder="Añade un texto a tu historia..." />
        </div>
        <SongPicker />
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Publicar Historia
          </Button>
        </DialogFooter>
      </form>
    );
}

export default function OrganizacionesPage() {
    const { user, jartinetData, loading, handleActionCompletion } = useAuth();
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const myOrgs = jartinetData?.organizaciones?.filter(org => (Array.isArray(org.gestores) && org.gestores.includes(user?.uid)) || user?.role === 'admin') || [];
    const myOrgIds = myOrgs.map(o => o.id);

    const postsQuery = useMemo(() => query(collection(db, 'social_posts'), where('authorType', '==', 'organizacion')), []);
    const { data: allPosts, isLoading: isLoadingPosts } = useCollection<SocialPost>(postsQuery);

    const storiesQuery = useMemo(() => query(collection(db, 'social_stories'), where('authorType', '==', 'organizacion'), where('expiresAt', '>', new Date())), []);
    const { data: allStories, isLoading: isLoadingStories } = useCollection<SocialStory>(storiesQuery);

    const orgPosts = allPosts?.filter(p => myOrgIds.includes(p.authorId)) || [];
    const orgStories = allStories?.filter(s => myOrgIds.includes(s.authorId)) || [];

    if (loading || isLoadingPosts || isLoadingStories) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>;

    const handleDeletePost = async (postId: string, authorId: string) => {
        setIsSubmitting(true);
        const result = await deleteSocialPost(postId, authorId, 'organizacion');
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            handleActionCompletion();
        } else {
            toast({ title: "Error", description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    const handleDeleteStory = async (storyId: string) => {
        setIsSubmitting(true);
        const result = await deleteSocialStory(storyId);
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            handleActionCompletion();
        } else {
            toast({ title: "Error", description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    return (
        <div className="space-y-6 pb-12">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-violet-500/15 via-card/40 to-card/20 p-8">
                <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />
                <div className="relative flex flex-wrap items-center justify-between gap-5">
                    <div className="flex items-center gap-5">
                        <div className="grid h-16 w-16 place-items-center rounded-2xl border border-violet-400/30 bg-violet-500/15 text-violet-300"><Landmark className="h-9 w-9" /></div>
                        <div>
                            <h1 className="font-headline text-4xl font-bold md:text-5xl">Mis Organizaciones</h1>
                            <p className="mt-2 max-w-2xl text-muted-foreground">Gestiona la red social (publicaciones e historias) de las cuentas que administras.</p>
                        </div>
                    </div>
                </div>
                {/* Chips de organizaciones */}
                <div className="relative mt-5 flex flex-wrap gap-2">
                    {myOrgs.map((o) => (
                        <span key={o.id} className="flex items-center gap-2 rounded-full border border-white/10 bg-black/30 py-1 pl-1 pr-3 text-sm">
                            <Avatar className="h-6 w-6"><AvatarImage src={o.escudoUrl} /></Avatar>{o.nombreCompleto}
                        </span>
                    ))}
                </div>
            </div>

            {myOrgs.length === 0 ? (
                <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                        No administras ninguna cuenta actualmente.
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="flex flex-wrap gap-3">
                        <Dialog>
                            <DialogTrigger asChild><Button size="lg" className="gap-2"><Plus className="h-5 w-5" /> Crear Publicación</Button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Crear Publicación</DialogTitle></DialogHeader>
                                <CreatePostForm organizaciones={myOrgs} allHermandades={jartinetData.hermandades || []} allBandas={jartinetData.bandas || []} onFormSuccess={handleActionCompletion} />
                            </DialogContent>
                        </Dialog>
                        <Dialog>
                            <DialogTrigger asChild><Button size="lg" variant="outline" className="gap-2"><Plus className="h-5 w-5" /> Crear Historia</Button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Crear Historia</DialogTitle></DialogHeader>
                                <CreateStoryForm organizaciones={myOrgs} onFormSuccess={handleActionCompletion} />
                            </DialogContent>
                        </Dialog>
                    </div>

                    <Tabs defaultValue="posts" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="posts">Publicaciones ({orgPosts.length})</TabsTrigger>
                            <TabsTrigger value="stories">Historias ({orgStories.length})</TabsTrigger>
                        </TabsList>
                        <TabsContent value="posts" className="space-y-4 pt-4">
                            {orgPosts.length > 0 ? orgPosts.map(post => (
                                <div key={post.id} className="flex items-start gap-4 p-3 rounded-md border bg-muted/50">
                                   <Image 
                                      src={Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl || ''} 
                                      alt={post.title} 
                                      className="w-20 h-20 rounded-md object-cover" 
                                    />
                                  <div className="flex-grow">
                                    <p className="font-semibold">{post.title}</p>
                                    <p className="text-xs text-muted-foreground">Por: {post.authorName}</p>
                                    <p className="text-xs text-muted-foreground line-clamp-2">{post.content}</p>
                                  </div>
                                   <div className="flex flex-col gap-1">
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" disabled={isSubmitting}><Trash2 className="h-4 w-4"/></Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>¿Eliminar esta publicación?</AlertDialogTitle>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                    <AlertDialogAction onClick={() => handleDeletePost(post.id, post.authorId)} className="bg-destructive hover:bg-destructive/80">Confirmar</AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                   </div>
                                </div>
                            )) : <p className="text-muted-foreground text-sm text-center py-4">No has subido publicaciones.</p>}
                        </TabsContent>
                        <TabsContent value="stories" className="space-y-2 pt-4">
                            {orgStories.length > 0 ? (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                    {orgStories.map(story => (
                                        <div key={story.id} className="relative aspect-[9/16] rounded-md overflow-hidden group">
                                            <Image src={Array.isArray(story.mediaUrl) ? story.mediaUrl[0] : story.mediaUrl} alt="Historia" className="object-cover w-full h-full" />
                                            <div className="absolute inset-0 bg-black/50 flex flex-col items-start justify-between p-2">
                                              <p className="text-white text-xs font-bold">{story.authorName}</p>
                                               <div className="self-end z-20">
                                                    <AlertDialog>
                                                        <AlertDialogTrigger asChild>
                                                        <Button size="icon" variant="destructive" className="h-7 w-7" disabled={isSubmitting}>
                                                                <Trash2 className="h-4 w-4"/>
                                                            </Button>
                                                        </AlertDialogTrigger>
                                                        <AlertDialogContent>
                                                            <AlertDialogHeader>
                                                                <AlertDialogTitle>¿Eliminar esta historia?</AlertDialogTitle>
                                                            </AlertDialogHeader>
                                                            <AlertDialogFooter>
                                                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                                <AlertDialogAction onClick={() => handleDeleteStory(story.id)} className="bg-destructive hover:bg-destructive/80">Confirmar</AlertDialogAction>
                                                            </AlertDialogFooter>
                                                        </AlertDialogContent>
                                                    </AlertDialog>
                                               </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="text-muted-foreground text-sm text-center py-4">No hay historias activas.</p>}
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
    );
}
